import { ColDef, GridOptions } from '@ag-grid-community/core';
import { Component, OnInit } from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { SubSink } from 'subsink';
import { AutoUnsubscribe } from '../../../core/auto-unsubscribe';
import { FormlyRequestProductsService } from './formly-request-products.service';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { BehaviorSubject, isObservable, Observable } from 'rxjs';
import { AgGridRequestProductActionRenderer } from './ag-grid-request-product-action-renderer/ag-grid-request-product-action-renderer.component';
import { RequestProductEditDialogComponent } from '../request-product-edit-dialog/request-product-edit-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { RequestTypeEnum } from '../../../shared/xatena-api/request-type';
import { SupplierContactCellRendererComponent } from './supplier-contact-cell-renderer/supplier-contact-cell-renderer.component';
import { MatCard } from '@angular/material/card';
import { AgGridModule } from '@ag-grid-community/angular';
import { MatFabButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

@AutoUnsubscribe()
@Component({
  selector: 'app-formly-request-products',
  templateUrl: './formly-request-products.component.html',
  styleUrls: ['./formly-request-products.component.scss'],
  providers: [FormlyRequestProductsService],
  standalone: true,
  imports: [
    MatCard,
    AgGridModule,
    MatFabButton,
    MatTooltip,
    MatIcon,
    TranslateModule,
  ],
})
export class FormlyRequestProductsComponent
  extends FieldType
  implements OnInit
{
  subs = new SubSink();

  public get isReadOnly(): boolean {
    return this.field?.form?.disabled == true;
  }

  actionColumnDef: ColDef = {
    headerName: _('DEMAND_REQUEST.DETAIL.PRODUCTS.GRID.COL.ACTION'),
    field: 'id',
    cellRenderer: 'requestProductActionRenderer',
    hide: false,
  };

  columnDefs: ColDef[] = [
    {
      headerName: _('DEMAND_REQUEST.DETAIL.PRODUCTS.GRID.COL.PRICE_BASE_UNIT'),
      field: 'priceBaseUnit',
      flex: 1,
      valueFormatter: this.requestProductsService.currencyValueFormatter.bind(
        this.requestProductsService
      ),
    },
    this.actionColumnDef,
  ];

  defaultColDef: ColDef = {
    resizable: true,
    sortable: true,
    headerValueGetter: this.requestProductsService.localizeHeader.bind(
      this.requestProductsService
    ),
  };

  gridOptions: GridOptions = {
    defaultColDef: this.defaultColDef,
    columnDefs: this.columnDefs,
    domLayout: 'autoHeight',

    components: {
      requestProductActionRenderer: AgGridRequestProductActionRenderer,
      supplierContactCellRenderer: SupplierContactCellRendererComponent,
    },
  };

  constructor(
    private requestProductsService: FormlyRequestProductsService,
    private spinnerService: NgxSpinnerService,
    private dialog: MatDialog
  ) {
    super();
    this.requestProductsService.bindGridEvents(this.gridOptions);
  }

  onAddClicked() {
    const dialogRef = this.dialog.open(RequestProductEditDialogComponent, {
      data: {
        requestId: this.requestProductsService.requestId$.value,
        currency: this.gridOptions.context.currency,
        fields: this.gridOptions.columnDefs?.map(
          m => (m as ColDef).field ?? ''
        ),
        requestTypeId:
          this.requestProductsService.requestTypeId$?.value ??
          RequestTypeEnum.CatalogProduct,
      },
    });

    this.subs.sink = dialogRef.afterClosed().subscribe(s => {
      this.requestProductsService.refreshRowDataCache();
    });
  }

  ngOnInit(): void {
    this.gridOptions.context = {};
    this.actionColumnDef.hide = this.isReadOnly;
    this.gridOptions.context.readOnly = this.isReadOnly;
    // sets currency used for col formatting
    if (isObservable(this.to.currency)) {
      this.subs.sink = this.to.currency.subscribe(s => {
        this.gridOptions.context.currency = s;

        this.requestProductsService.currency$.next(s as string);
      });
    } else if (this.to.currency as string) {
      this.gridOptions.context.currency = this.to.currency;
      this.requestProductsService.currency$.next(this.to.currency);
    }

    // adds cols to grid
    if (this.to.colDef && Array.isArray(this.to.colDef))
      this.columnDefs.unshift(...this.to.colDef);

    this.requestProductsService.requestId$.next(this.formControl.value);

    if (isObservable(this.to.requestTypeId))
      this.requestProductsService.requestTypeId$ = this.to
        .requestTypeId as BehaviorSubject<RequestTypeEnum>;
  }
}
